import { unauthorize } from '@/Infrastructure/Security/authorizationSlice';
import { resetStore } from '@/Infrastructure/applicationSlice';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RenderModalProps } from './types';

interface ModalState {
	renderQueue: RenderModalProps[];
}

const initialState: ModalState = {
	renderQueue: []
};

const modalSlice = createSlice({
	name: 'modalSlice',
	initialState,
	reducers: {
		/** Initiate an Action to queue the provided Modal Props for rendering by Modal Provider */
		queueModal(state, action: PayloadAction<Omit<RenderModalProps, 'id'>>) {
			const newModal = {
				id: Math.floor(Math.random() * 10000),
				...action.payload
			} as RenderModalProps;

			return {
				...state,
				renderQueue: [...(action.payload.replaceStack ? [] : state.renderQueue), newModal]
			};
		},

		/** Initiate an Action to queue a Controlled modal with a specified ID.  */
		queueControlledModal(state, action: PayloadAction<RenderModalProps>) {
			const renderedModals = [...state.renderQueue];

			/** If the given ID is already rendered, do not render again
			 *	This is a fail-safe to protect an effect controlling the render of the csn details
			 *  modal where react.strict causes the effect to trigger twice. Regardless, should be
			 *  handled this way to be cautious.
			 */
			if (renderedModals.find((x) => x.id === action.payload.id)) {
				return state;
			}

			return {
				...state,
				renderQueue: [...state.renderQueue, action.payload]
			};
		},

		/** Remove the top-most modal currently rendered (First-In, Last-Out) */
		removeModal(state) {
			const copy = [...state.renderQueue];

			return {
				...state,
				renderQueue: copy.slice(0, -1)
			};
		},

		/** Remove the top-most modal currently rendered (First-In, Last-Out) */
		removeControlledModal(state, action: PayloadAction<number>) {
			const copy = [...state.renderQueue.filter((x) => x.id !== action.payload)];

			return {
				...state,
				renderQueue: copy
			};
		}
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			({ type }) => type === resetStore.type || type === unauthorize.type,
			() => initialState
		);
	}
});

export const { queueModal, queueControlledModal, removeModal, removeControlledModal } = modalSlice.actions;

export default modalSlice;
